import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import { nanoid } from 'nanoid';
import eventBus from '@/foundations/services/eventBus';

type Props = {
  accept: string;
  multiple: boolean;
  allowedFiles?: string[];
  validFileName?: (fileName: string) => Promise<boolean>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'DropFile',
  props: {
    accept: null,
    multiple: { type: Boolean },
    allowedFiles: null,
    validFileName: null
  },
  emits: ["updateFiles"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (e: 'updateFiles', f: File[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  accept: string;
  multiple: boolean;
  allowedFiles?: string[];
  validFileName?: (fileName: string) => Promise<boolean>;
}



const isDragging = ref(false);
const addedFiles = ref<File[]>([]);
const addedFilesRef = ref(); // Template ref for the v-file-input component
const currentFiles = ref<File[]>([]);
const fileInputId = ref<string>(nanoid(5));
const fileTypePrefix = ref<string>(
  props.accept.endsWith('*') ? props.accept.substring(0, props.accept.length - 1) : props.accept,
);



const allowedPairNames = computed(() =>
  props.allowedFiles ? props.allowedFiles.slice(0, -1).map((file, i) => `${file}_${props.allowedFiles[i + 1]}`) : [],
);

// Function to properly clear the file input
const clearFileInput = (): void => {
  // Clear the Vue reactive ref
  addedFiles.value = [];

  // Clear the actual DOM input element to reset browser's file selection memory
  if (addedFilesRef.value) {
    // For Vuetify v-file-input, we need to access the internal input element
    const inputElement = addedFilesRef.value.$el?.querySelector('input[type="file"]');
    if (inputElement) {
      inputElement.value = '';
    }
  }
};

const onChange = async (): Promise<void> => {
  const addedFilelist: File[] = Array.isArray(addedFiles.value) ? addedFiles.value : [addedFiles.value];
  if (props.multiple) {
    const dups: File[] = [];
    addedFilelist.forEach((newFile) => {
      if (!currentFiles.value.find((f) => f.name === newFile.name)) {
        currentFiles.value.push(newFile);
      } else {
        dups.push(newFile);
      }
    });
    if (dups.length > 0) {
      eventBus.$emit('snackbar.open', `Ignoring already added files, '${dups.map((f) => f.name)}'`);
    }
    currentFiles.value.sort((a, b) => a.name.localeCompare(b.name));
  } else if (addedFilelist.length > 0) {
    currentFiles.value = [addedFilelist[0]];
  }
  emits('updateFiles', currentFiles.value);
};
const dragover = (e: any): void => {
  e.preventDefault();
  isDragging.value = true;
};

const dragleave = (): void => {
  isDragging.value = false;
};

const remove = (i: number): void => {
  currentFiles.value.splice(i, 1);
  // Clear the file input to allow re-uploading the same file
  clearFileInput();
  emits('updateFiles', currentFiles.value);
};

const removeFileName = (name: string): void => {
  remove(currentFiles.value.map((fObj) => fObj.name).indexOf(name));
  emits('updateFiles', currentFiles.value);
};

const getFileNameWithoutExtension = (name: string) => (name.indexOf('.') > -1 ? name.split('.')[0] : name);

const containsFileName = (name: string): boolean =>
  currentFiles.value.some((file) => {
    const fileName = getFileNameWithoutExtension(file.name);
    const names = allowedPairNames.value.includes(fileName) ? fileName.split('_') : [fileName];
    return names.includes(name);
  });

const validFileName = (name: string): boolean => {
  if (props.allowedFiles === undefined) {
    return false;
  }
  const fileName = getFileNameWithoutExtension(name);
  return props.allowedFiles.includes(fileName) || allowedPairNames.value.includes(fileName);
};

const drop = async (e: DragEvent) => {
  e.preventDefault();
  if (e.dataTransfer?.files) {
    addedFiles.value = [];
  }
  if (!props.multiple && e.dataTransfer.files.length > 1) {
    eventBus.$emit('snackbar.open', 'Only single file can be added!');
  } else {
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      const file = e.dataTransfer.files.item(i);
      if (file) {
        if (props.validFileName) {
          // eslint-disable-next-line no-await-in-loop
          const nameOK = await props.validFileName(file.name);
          if (nameOK) {
            addedFiles.value.push(file);
          }
        } else if (props.allowedFiles && !validFileName(file.name)) {
          eventBus.$emit(
            'snackbar.open',
            `Invalid file name, ${file.name}.\nThe file must match the filenames for the main language`,
          );
        } else if (!file.type.startsWith(fileTypePrefix.value)) {
          eventBus.$emit('snackbar.open', `Invalid file type, ${file.type}`);
        } else {
          addedFiles.value.push(file);
        }
      }
    }
  }
  onChange();
  isDragging.value = false;
};

const clearFiles = () => {
  while (currentFiles.value.length > 0) {
    currentFiles.value.pop();
  }
  // Clear the file input to allow re-uploading the same file
  clearFileInput();
  emits('updateFiles', currentFiles.value);
};

expose({
  clearFiles,
  removeFileName,
});

return { __sfc: true,props, isDragging, addedFiles, addedFilesRef, currentFiles, fileInputId, fileTypePrefix, emits, allowedPairNames, clearFileInput, onChange, dragover, dragleave, remove, removeFileName, getFileNameWithoutExtension, containsFileName, validFileName, drop, clearFiles }
}

})